import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
// @ts-nocheck
import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import TradeModal from './TradeModal';
import { pointsLabel } from '../../common/utils/pointsLabel';

/**
 * "Trades" tab on the user profile — visible only when the viewer IS the
 * profile owner. The backend's GET /api/point-system/trades already scopes
 * results to the authenticated actor, so a friend opening someone else's
 * profile and forcing the URL `/u/X/trades` still gets THEIR OWN trades
 * back, never X's — making the data leak surface effectively closed
 * regardless of what the frontend renders.
 *
 * That said, we ALSO redirect away on mount when the viewer is not the
 * profile owner so the UI doesn't show a confusingly-empty page or a
 * different user's trade history under someone else's username slug.
 */
export default class UserTradesPage extends UserPage {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "trades", []);
    _defineProperty(this, "err", '');
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.loadUser(m.route.param('username'));
  }
  show(user) {
    var _me$id, _user$id;
    super.show(user);
    const me = app.session.user;
    if (!me || Number((_me$id = me.id) == null ? void 0 : _me$id.call(me)) !== Number((_user$id = user.id) == null ? void 0 : _user$id.call(user))) {
      // Not the profile owner — bounce them away. Showing the tab
      // publicly would either leak self trades into someone else's
      // profile or render an empty page that confuses the visitor.
      m.route.set(app.route.user(user));
      return;
    }
    this.load();
  }
  async load() {
    this.loading = true;
    // Ver TradesPage.load(): limpar antes evita o painel de erro sobreviver
    // a um retry bem-sucedido.
    this.err = null;
    m.redraw();
    try {
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'GET',
        url: "".concat(apiUrl, "/point-system/trades")
      });
      this.trades = Array.isArray(res == null ? void 0 : res.data) ? res.data : [];
    } catch (e) {
      var _e$response;
      this.err = (e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) || app.translator.trans('ramon-point-system.forum.trades_page.load_failed');
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  content() {
    if (this.loading) return m(LoadingIndicator, null);
    const t = (k, v) => app.translator.trans('ramon-point-system.forum.trades_page.' + k, v);

    /*
     * Dedup defensivo por id — mesma justificativa de TradesPage.
     * Veja comentário lá: relato 2026-05-23 sobre "mesma trade aparecer
     * duas vezes na história" sem repro confiável.
     */
    const seen = new Set();
    const tradesUnique = this.trades.filter(tr => {
      const id = Number(tr == null ? void 0 : tr.id);
      if (!Number.isFinite(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    const pending = tradesUnique.filter(tr => tr.status === 'pending');
    const history = tradesUnique.filter(tr => tr.status !== 'pending');
    const canTrade = app.forum.attribute('pointSystemCanTrade') !== false;
    return m("div", {
      className: "PointSystemTradesPage PointSystemTradesPage--inProfile container"
    }, m("div", {
      className: "PointSystemTradesPage-header"
    }, m("div", {
      className: "PointSystemTradesPage-header-titleRow"
    }, m("h1", null, m("i", {
      className: "fas fa-handshake"
    }), " ", t('title')), canTrade && m(Button, {
      className: "Button Button--primary",
      onclick: () => {
        const StartTradeModal = require('./StartTradeModal').default;
        app.modal.show(StartTradeModal);
      }
    }, m("i", {
      className: "fas fa-plus"
    }), " ", t('start_new'))), m("p", {
      className: "helpText"
    }, t('subtitle'))), this.err && m("div", {
      className: "PointSystemTradesPage-error"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), m("span", null, this.err), m(Button, {
      className: "Button Button--primary",
      onclick: () => this.load()
    }, t('retry'))), m("section", {
      className: "PointSystemTradesPage-section"
    }, m("h2", null, t('pending_heading')), pending.length === 0 ? m("p", {
      className: "PointSystemTradesPage-empty"
    }, t('pending_empty')) : m("ul", {
      className: "PointSystemTradesPage-list"
    }, pending.map(tr => this.renderRow(tr, true)))), m("section", {
      className: "PointSystemTradesPage-section"
    }, m("h2", null, t('history_heading')), history.length === 0 ? m("p", {
      className: "PointSystemTradesPage-empty"
    }, t('history_empty')) : m("ul", {
      className: "PointSystemTradesPage-list"
    }, history.map(tr => this.renderRow(tr, false)))));
  }
  renderRow(trade, isPending) {
    const t = (k, v) => app.translator.trans('ramon-point-system.forum.trades_page.' + k, v);
    const youAre = trade.youAre;
    const other = youAre === 'initiator' ? trade.recipient : trade.initiator;
    const yourItems = trade.items.filter(it => Number(it.ownerId) === Number(youAre === 'initiator' ? trade.initiator.id : trade.recipient.id));
    const theirItems = trade.items.filter(it => Number(it.ownerId) === Number(other == null ? void 0 : other.id));
    const yourPoints = youAre === 'initiator' ? trade.initiatorPoints : trade.recipientPoints;
    const theirPoints = youAre === 'initiator' ? trade.recipientPoints : trade.initiatorPoints;
    const accepted = youAre === 'initiator' ? trade.initiatorAccepted : trade.recipientAccepted;
    const theirAccepted = youAre === 'initiator' ? trade.recipientAccepted : trade.initiatorAccepted;
    const statusClass = "is-".concat(trade.status) + (isPending && accepted && !theirAccepted ? ' is-waiting' : '');
    return m("li", {
      className: "PointSystemTradesPage-row ".concat(statusClass),
      key: "trade-".concat(trade.id)
    }, m("div", {
      className: "PointSystemTradesPage-row-party"
    }, other != null && other.avatarUrl ? m("img", {
      className: "PointSystemTradesPage-row-avatar",
      src: other.avatarUrl,
      alt: ""
    }) : m("span", {
      className: "PointSystemTradesPage-row-avatar PointSystemTradesPage-row-avatar--placeholder",
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-user"
    })), m("div", null, m("strong", null, (other == null ? void 0 : other.displayName) || (other == null ? void 0 : other.username) || '—'), m("div", {
      className: "PointSystemTradesPage-row-time"
    }, this.formatTime(trade.updatedAt)))), m("div", {
      className: "PointSystemTradesPage-row-summary"
    }, m("div", {
      className: "PointSystemTradesPage-row-side"
    }, m("span", {
      className: "muted"
    }, t('your_side')), m("span", null, yourPoints > 0 ? "".concat(Number(yourPoints).toLocaleString(), " ").concat(pointsLabel(app)) : '', ' ', yourItems.length > 0 ? "+ ".concat(t('items_short', {
      count: yourItems.length
    })) : '', yourPoints === 0 && yourItems.length === 0 ? m("em", null, "\u2014") : '')), m("i", {
      className: "fas fa-arrows-alt-h"
    }), m("div", {
      className: "PointSystemTradesPage-row-side"
    }, m("span", {
      className: "muted"
    }, t('their_side')), m("span", null, theirPoints > 0 ? "".concat(Number(theirPoints).toLocaleString(), " ").concat(pointsLabel(app)) : '', ' ', theirItems.length > 0 ? "+ ".concat(t('items_short', {
      count: theirItems.length
    })) : '', theirPoints === 0 && theirItems.length === 0 ? m("em", null, "\u2014") : ''))), m("div", {
      className: "PointSystemTradesPage-row-status"
    }, m("span", {
      className: "PointSystemTradesPage-pill ".concat(statusClass)
    }, t('status_' + trade.status)), isPending && m(Button, {
      className: "Button Button--primary",
      onclick: () => app.modal.show(TradeModal, {
        tradeId: trade.id
      })
    }, m("i", {
      className: "fas fa-arrow-right"
    }), " ", t('open'))));
  }
  formatTime(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch (_unused) {
      return '';
    }
  }
}
flarum.reg.add('ramon-point-system', 'forum/components/UserTradesPage', UserTradesPage);